import React, { useEffect, useState } from 'react';
import {
  alpha, Box, Button, FormControl, TextField, Typography, useTheme, Checkbox,
  Autocomplete,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { APICreateOperator, APIUpdateOperator } from '../../utils/apiCalled';
import BackButton from '../backButton';

const generateOperatorID = (operatorName) => {
  const nameWithoutVowels = operatorName
    .replace(/[aeiou]/gi, '')
    .replace(/\s/g, '')
    .toUpperCase();

  const date = new Date();
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid createdAt date');
  }

  const timestamp = date
    .toISOString()
    .replace(/[-T:.Z]/g, '')
    .slice(0, 14);
  return `${nameWithoutVowels}${timestamp}`;
};

export default function PageOperator({
  operator,
  setOpen,
  setSuccMsg,
  setErrMsg,
  setVariant,
  setMainMsg,
  setModalTriggerAPICalled,
  handleCloseMoreOperators,
  zones,
  role,
}) {
  const [operatorName, setOperatorName] = useState('');
  const [operatorID, setOperatorID] = useState('');
  const [operatorPostcode, setOperatorPostcode] = useState('');
  const [operatorTimezone, setOperatorTimezone] = useState('');
  const [assignedZones, setAssignedZones] = useState([]);
  const [posLoading, setPosLoading] = useState(false);
  useEffect(() => {
    if (operator) {
      setOperatorName(operator.operatorName || '');
      setOperatorID(operator.operatorID || '');
      setOperatorPostcode(operator.operatorPostcode || '');
      setOperatorTimezone(operator.operatorTimezone || '');
      setAssignedZones(operator.assignedZones || []);
    }
  }, [operator]);

  // Auto-generate Operator ID when name and postcode are available for new operators
  useEffect(() => {
    if (!operator) { // Only for new operators
      if (operatorName) {
        const newId = generateOperatorID(operatorName);
        setOperatorID(newId);
      } else {
        setOperatorID('');
      }
    }
  }, [operatorName, operatorPostcode, operator]);

  const theme = useTheme();

  const fontHeaderStyle = {
    color: theme.palette.grey[700],
    fontWeight: 'fontWeightMedium',
    mr: 5,
    minWidth: '250px',
  };

  const formControlStyle = {
    mb: 3,
    display: 'flex',
    flexDirection: 'row',
  };

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const buttonStyle = {
    border: '1px solid',
    borderColor: 'grey.300',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    color: theme.palette.grey[700],
    backgroundColor: 'background.paper',
    '&:hover': {
      bgcolor: alpha(theme.palette.grey[500], 0.08),
    },
  };

  const confirmButtonStyle = {
    border: 'none',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    bgcolor: theme.palette.primary.main,
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.90),
    },
    color: 'white',
    fontWeight: 'fontWeightMedium',
    '&.Mui-disabled': {
      bgcolor: theme.palette.primary.main,
      color: 'white',
      opacity: '0.6',
    },
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case 'operatorName':
        setOperatorName(value);
        break;
      case 'operatorID':
        setOperatorID(value);
        break;
      case 'assignedZones':
        setAssignedZones(value);
        break;
      case 'operatorPostcode':
        setOperatorPostcode(value);
        break;
      case 'operatorTimezone':
        setOperatorTimezone(value);
        break;
      default:
        break;
    }
  };

  const removeAllState = () => {
    setOperatorName('');
    setOperatorID('');
    setOperatorPostcode('');
    setOperatorTimezone('');
    setAssignedZones([]);
  };

  const apiAddEditCalled = async (value, id) => {
    try {
      if (operator && operator._id) {
        // UPDATE OPERATOR
        const result = await APIUpdateOperator(setPosLoading, id, value);
        setPosLoading(false);
        if (result.status === 200) {
          removeAllState();
          setOpen(true);
          setVariant('success');
          setSuccMsg(`Operator ${value.operatorName} has sucessfully been edited`);
          setMainMsg('Operator Successfully Edited');
          setModalTriggerAPICalled(true);
        } else {
          removeAllState();
          const errorMessage = result.response?.data?.message || 'Failed to update operator. Please try again.';
          setOpen(true);
          setVariant('error');
          setErrMsg(errorMessage);
          setMainMsg('Operator Submission Unsuccessful');
          setModalTriggerAPICalled(true);
        }
      } else {
        // CREATE OPERATOR
        const result = await APICreateOperator(setPosLoading, value);
        setPosLoading(false);
        if (result.status === 200) {
          removeAllState();
          setOpen(true);
          setVariant('success');
          setSuccMsg(`Operator ${value.operatorName} has sucessfully been created`);
          setMainMsg('Operator Successfully Created');
          setModalTriggerAPICalled(true);
        } else {
          removeAllState();
          const errorMessage = result.response?.data?.message || 'Failed to create operator. Please try again.';
          setOpen(true);
          setVariant('error');
          setErrMsg(errorMessage);
          setMainMsg('Operator Submission Unsuccessful');
          setModalTriggerAPICalled(true);
        }
      }
    } catch (err) {
      setPosLoading(false);
      removeAllState();
      setOpen(true);
      setVariant('error');
      setErrMsg(`Error! ${err.message || 'Please try again'}`);
      setMainMsg('Operator Submission Unsuccessful');
    }
  };

  const handleSubmit = async () => {
    const validationError = (() => {
      if (!operatorName) return 'Operator Name is required';
      if (!operatorTimezone) return 'Operator Timezone is required';
      return null;
    })();

    if (validationError) {
      setOpen(true);
      setVariant('error');
      setErrMsg(validationError);
      return;
    }

    const payload = {
      operatorName,
      operatorTimezone,
      operatorID,
      assignedZones,
    };

    await apiAddEditCalled(payload, operator ? operator._id : null);
    handleCloseMoreOperators();
  };

  const isFormInvalid = () => {
    if (operator) {
      const zonesLengthChanged = assignedZones.length !== (operator.assignedZones?.length || 0);

      const isUnchanged = operatorName === operator.operatorName
        && operatorTimezone === operator.operatorTimezone
        && JSON.stringify(assignedZones) === JSON.stringify(operator.assignedZones || []);

      // Enable confirm if zone length changed
      if (zonesLengthChanged) return false;

      if (isUnchanged) return true;
    }
    // For new operators, still enforce required fields
    if (!operatorName || !operatorTimezone || !assignedZones.length) return true;
    return false;
  };
  return (
    <>
      <Box sx={{ mt: 2 }} />
      <BackButton back={handleCloseMoreOperators} type="operator" />
      <Box sx={{
        mt: 2, pb: 4, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
      }}
      >
        <Box>
          <Typography variant="h4">{operator ? 'Edit Operator' : 'Create Operator'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'right' }}>
          <Button sx={buttonStyle} onClick={() => handleCloseMoreOperators()}>
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              Cancel
            </Typography>
          </Button>
          <Button sx={confirmButtonStyle} disabled={isFormInvalid()} onClick={handleSubmit}>
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              Confirm
            </Typography>
          </Button>
        </Box>
      </Box>
      <Box sx={{
        padding: 1, borderTop: '1px solid', borderColor: theme.palette.grey[300], display: 'flex',
      }}
      >
        <Box sx={{ width: '70%', mt: 2 }}>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Operator Name
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField label="Operator Name" name="operatorName" value={operatorName} onChange={handleInputChange} sx={{ flexGrow: 1 }} />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Operator ID
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField disabled label="Auto-Generated Operator ID" name="operatorID" value={operatorID} onChange={handleInputChange} sx={{ flexGrow: 1 }} />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Assign Zone(s)
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Autocomplete
              multiple
              id="assign-zones"
              options={zones}
              disableCloseOnSelect
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={zones.filter((z) => assignedZones.includes(z._id))}
              onChange={(event, newValue) => {
                const newZoneIds = newValue.map((z) => z._id);
                setAssignedZones(newZoneIds);
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              )}
              style={{ width: '100%' }}
              renderInput={(params) => (
                <TextField {...params} label="Assign Zones" placeholder="Select zones" />
              )}
            />
          </FormControl>
          <FormControl sx={formControlStyle}>
            <Typography sx={fontHeaderStyle}>
              Operator Timezone
              {' '}
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField label="Operator Timezone" name="operatorTimezone" value={operatorTimezone} onChange={handleInputChange} sx={{ flexGrow: 1 }} />
          </FormControl>
        </Box>
      </Box>
    </>
  );
}
